import { useState, useEffect, useRef } from 'react';
import ScrollReveal from './ScrollReveal';

const stats = [
  { end: 100, suffix: '%', label: 'Campañas profesionales', icon: 'ri-check-double-line', prefix: '' },
  { end: 200, suffix: '%', label: 'Más clientes potenciales', icon: 'ri-user-add-line', prefix: '' },
  { end: 3.2, suffix: '', label: 'Mejor ROAS promedio', icon: 'ri-funds-line', prefix: '+' },
  { end: 5, suffix: '', label: 'Mayor productividad', icon: 'ri-speed-up-line', prefix: '+' },
  { end: 100, suffix: '%', label: 'Confianza al anunciar', icon: 'ri-emotion-happy-line', prefix: '' },
];

function AnimatedCounter({ end, suffix, prefix, duration = 2000 }: { end: number; suffix: string; prefix: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || hasAnimated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasAnimated]);

  useEffect(() => {
    if (!hasAnimated) return;

    const hasDecimal = end % 1 !== 0;

    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const raw = eased * end;
      setCount(hasDecimal ? Math.round(raw * 10) / 10 : Math.floor(raw));
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [hasAnimated, end, duration]);

  return (
    <div ref={ref} className="text-center">
      <div className="font-heading text-4xl md:text-5xl font-bold text-[#E53935] mb-1">
        {prefix}{count}{suffix}
      </div>
    </div>
  );
}

export default function Results() {
  return (
    <section className="py-20 md:py-28 bg-[#FFFFFF]">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        <ScrollReveal>
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-wider uppercase text-[#C8A24A] mb-3">
              Impacto
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#111111]">
              Resultados esperados
            </h2>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 max-w-[1100px] mx-auto">
          {stats.map((stat, idx) => (
            <ScrollReveal key={stat.label} delay={idx * 100}>
              <div className="bg-[#F7F8FA] rounded-2xl p-6 border border-gray-100 hover:border-[#E53935]/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="w-10 h-10 rounded-xl bg-[#E53935]/5 flex items-center justify-center mb-4 mx-auto">
                  <i className={`${stat.icon} text-[#E53935] text-lg`}></i>
                </div>
                <AnimatedCounter end={stat.end} suffix={stat.suffix} prefix={stat.prefix} />
                <p className="text-xs text-[#666666] text-center mt-2 leading-snug">{stat.label}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}